import React, { useRef, useState } from "react";
import { Backdrop } from "@mui/material";
import { organizationsApi, usersApi } from "api";
import { toDataUrl } from "common";
import { putPrioritiesOnServer } from "common/utilities";
import { useSnackbar, useUser, useVersions } from "hooks";
import { Priority } from "hooks/usePriorities";
import {
  GroupDialog,
  InviteUserDialog,
  OrganizationDialog,
  RoleDialog,
} from "components/dialogs";
import RobotsShop from "components/RobotsShop";
import { Organization } from "model/organizationModel";

interface UseOrganizationActionsParams {
  organizationId?: number | null;
  selectedOrganization?: Organization | null;
  getOrganizations: React.MutableRefObject<Function | undefined>;
  getGroups: React.MutableRefObject<Function | undefined>;
  getInvitedUsers: React.MutableRefObject<Function | undefined>;
  getRoles: React.MutableRefObject<Function | undefined>;
  getRobots: React.MutableRefObject<Function | undefined>;
}

const useOrganizationActions = ({
  organizationId,
  getOrganizations,
  getGroups,
  getInvitedUsers,
  getRoles,
  getRobots,
  selectedOrganization,
}: UseOrganizationActionsParams) => {
  const user = useUser();
  const { sendSnack } = useSnackbar();
  const { updateVersion } = useVersions();
  const clickable = useRef(true);
  const [organization, setOrganization] = useState(false);
  const [organizationEdit, setOrganizationEdit] = useState<Organization | null>(null);
  const [group, setGroup] = useState<{} | null>(null);
  const [role, setRole] = useState<{} | null>(null);
  const [invitingUser, setInvitingUser] = useState(false);
  const [robotsShopOpen, setRobotsShopOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const releaseClick = () => {
    setTimeout(() => {
      clickable.current = true;
    });
  };

  const createNewOrganization = async (
    imageUrl: string | null,
    name: string | null,
    priorities: Priority[]
  ) => {
    if (!clickable.current) return;
    clickable.current = false;
    setLoading(true);
    try {
      const icon = imageUrl ? await toDataUrl(imageUrl) : null;
      const createRes = await organizationsApi.post("", { name, icon });
      setOrganization(false);
      await putPrioritiesOnServer(priorities, createRes.data.id);
      sendSnack({ message: `${createRes.data.responsemessage}`, type: "success" });
      getOrganizations.current && getOrganizations.current();
    } catch {
    } finally {
      releaseClick();
      setLoading(false);
    }
  };

  const createNewGroup = async (imageUrl: string | null, name: string | null) => {
    if (!organizationId || !clickable.current) return;
    clickable.current = false;
    setLoading(true);
    try {
      const icon = imageUrl ? await toDataUrl(imageUrl) : null;
      const createRes = await organizationsApi.post(`${organizationId}/groups`, { name, icon });
      setGroup(null);
      sendSnack({ message: `${createRes.data.responsemessage}`, type: "success" });
      getGroups.current && getGroups.current();
    } catch {
    } finally {
      releaseClick();
      setLoading(false);
    }
  };

  const createNewRole = async (imageUrl: string | null, name: string | null) => {
    if (!organizationId || !clickable.current) return;
    clickable.current = false;
    setLoading(true);
    try {
      const icon = imageUrl ? await toDataUrl(imageUrl) : null;
      const createRes = await organizationsApi.post(`${organizationId}/roles`, { name, icon });
      setRole(null);
      sendSnack({ message: `${createRes.data.responsemessage}`, type: "success" });
      getRoles.current && getRoles.current();
    } catch {
    } finally {
      releaseClick();
      setLoading(false);
    }
  };

  const handleInviteUser = async (email: string | null) => {
    if (!organizationId || !clickable.current) return;
    clickable.current = false;
    setLoading(true);
    try {
      const createRes = await usersApi.post(
        `${user?.userId}/organizations/${organizationId}/inviteusers/dashboard`,
        { email: [email] }
      );
      setInvitingUser(false);
      sendSnack({ message: `${createRes.data.responsemessage}`, type: "success" });
      getInvitedUsers.current && getInvitedUsers.current();
    } catch {
    } finally {
      releaseClick();
      setLoading(false);
    }
  };

  const updateOrganization = async (
    imageUrl: string | null,
    name: string | null
  ) => {
    if (!organizationEdit) return;
    setLoading(true);
    try {
      const icon = imageUrl ? await toDataUrl(imageUrl) : null;
      const updateApi = await organizationsApi.put(`${organizationEdit.id}`, { name, icon });
      updateVersion(organizationEdit.id);
      setOrganizationEdit(null);
      sendSnack({ message: `${updateApi.data.responsemessage}`, type: "success" });
      getOrganizations.current && getOrganizations.current();
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const leaveOrganization = async () => {
    if (!organizationEdit) return;
    setLoading(true);
    try {
      const deleteApi = await organizationsApi.delete(
        `/${organizationEdit.id}/users/${user?.userId}`
      );
      sendSnack({ message: `${deleteApi.data.responsemessage}`, type: "success" });
      setOrganizationEdit(null);
      getOrganizations.current && getOrganizations.current();
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const updatePriorities = async (priorities: Priority[]): Promise<Priority[]> => {
    if (!organizationEdit) throw new Error("No organization selected");
    setLoading(true);
    try {
      await putPrioritiesOnServer(priorities, organizationEdit.id);
      const { data } = await organizationsApi.get(`${organizationEdit.id}/priorities`);
      updateVersion(organizationEdit.id);
      return data.priorities;
    } finally {
      setLoading(false);
    }
  };

  const dialogs = (
    <React.Fragment>
      <Backdrop open={robotsShopOpen} sx={{ zIndex: (_) => _.zIndex.drawer + 1 }} />
      <RobotsShop
        open={robotsShopOpen}
        onClose={() => setRobotsShopOpen(false)}
        organizationId={organizationId}
        onRobotUpdate={() => getRobots.current && getRobots.current()}
      />
      <OrganizationDialog
        loading={loading}
        open={organization}
        onClose={() => setOrganization(false)}
        organization={null}
        onSave={createNewOrganization}
      />
      <OrganizationDialog
        loading={loading}
        open={Boolean(organizationEdit)}
        onClose={() => setOrganizationEdit(null)}
        organization={organizationEdit}
        onSave={updateOrganization}
        onSavePriorities={updatePriorities}
        onDelete={leaveOrganization}
      />
      <GroupDialog
        loading={loading}
        open={Boolean(group)}
        onClose={() => setGroup(null)}
        group={group}
        onSave={createNewGroup}
      />
      <RoleDialog
        loading={loading}
        open={Boolean(role)}
        onClose={() => setRole(null)}
        role={role}
        onSave={createNewRole}
      />
      <InviteUserDialog
        loading={loading}
        open={invitingUser}
        onClose={() => setInvitingUser(false)}
        onSave={handleInviteUser}
      />
    </React.Fragment>
  );

  return {
    dialogs,
    openCreateOrganization: () => setOrganization(true),
    openCreateGroup: () => setGroup({}),
    openCreateRole: () => setRole({}),
    openInviteUser: () => setInvitingUser(true),
    openRobotsShop: () => setRobotsShopOpen(true),
    openOrganizationSettings: () => {
      if (selectedOrganization?.administrator) {
        setOrganizationEdit(selectedOrganization);
      }
    },
  };
};

export default useOrganizationActions;
